import { Component, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd, ActivatedRoute, RouterModule } from '@angular/router';
import { filter, distinctUntilChanged } from 'rxjs/operators';
import { MatIconModule } from '@angular/material/icon';

export interface Breadcrumb {
  label: string;
  url: string;
}

@Component({
  selector: 'app-breadcrumb',
  standalone: true,
  imports: [CommonModule, RouterModule, MatIconModule],
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss']
})
export class BreadcrumbComponent implements OnInit {
  breadcrumbs: Breadcrumb[] = [];
  @Input() activePanel?: string | null;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.breadcrumbs = this.buildBreadcrumbs(this.activatedRoute.root);
        this.appendActivePanel();
      });

    // Initial breadcrumb
    this.breadcrumbs = this.buildBreadcrumbs(this.activatedRoute.root);
    this.appendActivePanel();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activePanel']) {
      // Ensure breadcrumbs are built then append activePanel label
      if (!this.breadcrumbs || this.breadcrumbs.length === 0) {
        this.breadcrumbs = this.buildBreadcrumbs(this.activatedRoute.root);
      }
      this.appendActivePanel();
    }
  }

  private appendActivePanel(): void {
    const panel = this.activePanel;
    if (panel && panel.trim() !== '') {
      const label = this.formatLabel(panel) || panel.charAt(0).toUpperCase() + panel.slice(1);
      if (!label || label.trim() === '') {
        return;
      }
      // If the last breadcrumb was an appended panel (url is empty), replace it.
      const lastIndex = this.breadcrumbs.length - 1;
      if (lastIndex >= 0 && this.breadcrumbs[lastIndex].url === '') {
        // Replace existing appended panel label
        this.breadcrumbs[lastIndex] = { label, url: '' };
      } else {
        this.breadcrumbs.push({ label, url: '' });
      }
    }
  }

  private buildBreadcrumbs(
    route: ActivatedRoute,
    url: string = '',
    breadcrumbs: Breadcrumb[] = []
  ): Breadcrumb[] {
    // Add home as first breadcrumb
    if (breadcrumbs.length === 0) {
      breadcrumbs.push({ label: 'Home', url: '/dashboard' });
    }

    const children: ActivatedRoute[] = route.children;

    if (children.length === 0) {
      return breadcrumbs;
    }

    for (const child of children) {
      const routeURL: string = child.snapshot.url
        .map(segment => segment.path)
        .join('/');

      if (routeURL !== '') {
        url += `/${routeURL}`;

        // Get the breadcrumb label from route data
        let label = child.snapshot.data['breadcrumb'];
        
        // If no breadcrumb data, try to format the URL
        if (!label) {
          // For dynamic segments (like IDs), check the parent route's breadcrumb
          const segments = routeURL.split('/');
          if (segments.length > 0) {
            // Use only the first segment for label generation
            label = this.formatLabel(segments[0]);
          }
        }
        
        // Check if this is a detail/action route (view, edit, download)
        const isDetailRoute = routeURL.startsWith('view/') || 
                              routeURL.startsWith('card/');
        
        // Add parent breadcrumb for detail routes
        if (isDetailRoute && label && label.trim() !== '') {
          // Add "My Cards" as parent if not already present
          const hasCardsParent = breadcrumbs.some(b => b.label === 'My Cards');
          if (!hasCardsParent) {
            breadcrumbs.push({ label: 'My Cards', url: '/cards' });
          }
        }
        
        // Only add breadcrumb if label is not empty and not 'Home'
        if (label && label !== 'Home' && label.trim() !== '') {
          breadcrumbs.push({ label: label.trim(), url });
        }
      }

      return this.buildBreadcrumbs(child, url, breadcrumbs);
    }

    // Filter out any breadcrumbs with empty labels before returning
    return breadcrumbs.filter(b => b.label && b.label.trim() !== '');
  }

  private formatLabel(url: string): string {
    // Map routes to readable labels
    const labelMap: { [key: string]: string } = {
      'dashboard': 'Dashboard',
      'cards': 'My Cards',
      'card': 'Card Details',
      'edit-card': 'Edit Card',
      'edit': 'Edit',
      'about': 'About',
      'analytics': 'Analytics',
      'employees': 'Employees',
  'gallery': 'Gallery',
  'payment': 'Payments',
      'listing': 'Listings',
      'members': 'Members',
      'payments': 'Payments',
      'settings': 'Settings',
      'download': 'Download',
      'ai-chat': 'AI Content Generator',
      'viewpage': 'View Card',
      'view': 'View Card'
    };

    // Check if it's a UUID pattern (dynamic ID)
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (uuidPattern.test(url)) {
      // Return empty string to skip this segment
      return '';
    }

    // Check if the URL contains a UUID (for paths like "card/uuid")
    const urlParts = url.split('/');
    if (urlParts.length > 1) {
      // Return label for the first part only
      return labelMap[urlParts[0]] || '';
    }

    return labelMap[url] || url.charAt(0).toUpperCase() + url.slice(1);
  }
}
